import formatHrefURL from 'ha/utils/formatHrefURL';
import encodeSearchQuery from 'ha/utils/encodeSearchQuery';
import { buildParams, buildSearchQuery } from '@ha/search-query';
import isAbsoluteUrl from 'ha/utils/isAbsoluteUrl';
import { Language } from 'ha/i18n/types';
import { ConversationsFilters } from 'ha/constants/ConversationsFilters';
import { HowItWorksUrls } from 'ha/constants/HowItWorksUrls';
import { PricingUrls } from 'ha/constants/PricingPageSlug';

import { matchPattern } from 'react-router/es/PatternUtils';
import { BookingFlowSteps } from 'ha/pages/ContactAdvertiser/types';
import { socialUrls } from './constants';

export const PaymentsUrls = {
  tenant: {
    [Language.en]: '/online-rent-payment',
    [Language.nl]: '/nl/online-huur-betalen',
    [Language.de]: '/de/online-mietzahlung',
    [Language.it]: '/it/pagamento-affitto-online',
    [Language.fr]: '/fr/paiement-locatif-en-ligne',
    [Language.es]: '/es/pago-alquiler-online',
  },
  landlord: {
    [Language.en]: '/online-rent-collection',
    [Language.nl]: '/nl/online-huurinkomsten-beheren',
    [Language.de]: '/de/online-mieteinzuege',
    [Language.it]: '/it/riscuoti-gli-affitti-online',
    [Language.fr]: '/fr/loyers-en-ligne',
    [Language.es]: '/es/cobro-alquiler-online',
  },
};

const landlordsLangToPath = {
  [Language.nl]: 'Nederland/verhuurders',
  [Language.de]: 'Deutschland/vermieter',
};

const communityLangToPath = {
  [Language.en]: 'community',
};

const helpCenterLangToPath = {
  [Language.en]: 'help-center',
  [Language.it]: 'centro-di-assistenza',
  [Language.es]: 'centro-de-ayuda',
  [Language.de]: 'hilfe-center',
  [Language.fr]: 'centre-d-assistance',
  [Language.nl]: 'helpcenter',
};

const becomeAnAdvertiserLangToPath = {
  [Language.en]: '/become-an-advertiser',
  [Language.it]: '/dai-in-affitto',
  [Language.es]: '/hazte-anunciante',
  [Language.de]: '/inserent-werden',
  [Language.fr]: '/devenir-annonceur',
  [Language.nl]: '/verhuurder-worden',
};

const rentRadarLanguanges = [
  Language.de,
  Language.nl,
  Language.es,
  Language.it,
];

const rentRadarLanguangesWithLeadFormSupported = [Language.de, Language.nl];

const krugerLangToPath = {
  [Language.en]:
    '/housinganywhere-acquires-german-classifieds-website-studenten-wg',
  [Language.de]:
    '/de/housinganywhere-uebernimmt-die-deutsche-website-fuer-kleinanzeigen-studenten-wg',
  [Language.es]:
    '/es/housinganywhere-adquiere-studenten-wg-el-sitio-web-aleman-de-anuncios-clasificados',
  [Language.it]:
    '/it/housinganywhere-acquisisce-studenten-wg-il-sito-di-annunci-immobiliari-tedesco',
  [Language.nl]:
    '/nl/housinganywhere-neemt-duitse-marktplaats-studenten-wg-over',
};

const answersLangToPath = {
  [Language.en]: 'https://answers.housinganywhere.com/en/',
  [Language.es]: 'https://answers.housinganywhere.com/es/',
  [Language.fr]: 'https://answers.housinganywhere.com/fr/',
  [Language.de]: 'https://answers.housinganywhere.com/de/',
  [Language.it]: 'https://answers.housinganywhere.com/it/',
  [Language.pt]: 'https://answers.housinganywhere.com/pt/',
  [Language.nl]: 'https://answers.housinganywhere.com/nl/',
};

const calendarSyncGuidePathByLang = {
  [Language.en]: '/calendar-sync-guide',
  [Language.es]: '/calendario-sincronizar-guia',
  [Language.fr]: '/synchronisation-calendrier-guide',
  [Language.de]: '/kalender-synchronisieren-leitfaden',
  [Language.it]: '/guida-sincronizzare-calendario',
  [Language.nl]: '/hoe-kalender-synchroniseren',
};

export const CancellationPolicyHelpUrls = {
  [Language.de]:
    'https://answers.housinganywhere.com/de/articles/2185219-stornierungsbedingungen-fur-mieter',
  [Language.en]:
    'https://answers.housinganywhere.com/en/articles/2185219-cancellation-policy-for-tenants',
  [Language.es]:
    'https://answers.housinganywhere.com/es/articles/2185219-politica-de-cancelacion-para-inquilinos',
  [Language.fr]:
    'https://answers.housinganywhere.com/fr/articles/2185219-politique-d-annulation-pour-les-locataires',
  [Language.it]:
    'https://answers.housinganywhere.com/it/articles/2185219-termini-di-cancellazione-per-inquilini',
  [Language.nl]:
    'https://answers.housinganywhere.com/nl/articles/2185219-annuleringsvoorwaarden-voor-huurders',
  [Language.pt]:
    'https://answers.housinganywhere.com/pt/articles/2185219-politica-de-cancelamento-para-inquilinos',
};

export const AdvancedPricingSpecialOfferArticle = {
  [Language.de]: 'https://answers.housinganywhere.com/de/articles/4967880',
  [Language.en]: 'https://answers.housinganywhere.com/en/articles/4967880',
  [Language.es]: 'https://answers.housinganywhere.com/es/articles/4967880',
  [Language.fr]: 'https://answers.housinganywhere.com/fr/articles/4967880',
  [Language.it]: 'https://answers.housinganywhere.com/it/articles/4967880',
  [Language.nl]: 'https://answers.housinganywhere.com/nl/articles/4967880',
};

export const AdvancedPricingBannerLink = {
  [Language.de]: 'https://answers.housinganywhere.com/de/collections/2272851',
  [Language.en]: 'https://answers.housinganywhere.com/en/collections/2272851',
  [Language.es]: 'https://answers.housinganywhere.com/es/collections/2272851',
  [Language.fr]: 'https://answers.housinganywhere.com/fr/collections/2272851',
  [Language.it]: 'https://answers.housinganywhere.com/it/collections/2272851',
  [Language.nl]: 'https://answers.housinganywhere.com/nl/collections/2272851',
};

export const AdvancedPricingDescriptionLink = {
  [Language.de]: 'https://answers.housinganywhere.com/de/articles/4967879',
  [Language.en]: 'https://answers.housinganywhere.com/en/articles/4967879',
  [Language.es]: 'https://answers.housinganywhere.com/es/articles/4967879',
  [Language.fr]: 'https://answers.housinganywhere.com/fr/articles/4967879',
  [Language.it]: 'https://answers.housinganywhere.com/it/articles/4967879',
  [Language.nl]: 'https://answers.housinganywhere.com/nl/articles/4967879',
};

export const StructuredCostsHelpUrls = {
  [Language.de]:
    'https://answers.housinganywhere.com/de/articles/4591900-was-sind-kosten-fur-zusatzleistungen-und-wie-veroffentliche-ich-sie',
  [Language.en]:
    'https://answers.housinganywhere.com/en/articles/4591900-what-are-costs-for-extra-services-and-how-do-i-publish-them',
  [Language.es]:
    'https://answers.housinganywhere.com/es/articles/4591900-que-son-los-costes-por-servicios-extra-y-como-los-publico',
  [Language.fr]:
    'https://answers.housinganywhere.com/fr/articles/4591900-que-sont-les-frais-supplementaires-et-comment-les-ajouter',
  [Language.it]:
    'https://answers.housinganywhere.com/it/articles/4591900-cosa-sono-i-costi-per-servizi-aggiuntivi-e-come-li-specifico',
  [Language.nl]:
    'https://answers.housinganywhere.com/nl/articles/4591900-wat-zijn-kosten-voor-extra-diensten-en-hoe-publiceer-ik-die',
  [Language.pt]:
    'https://answers.housinganywhere.com/pt/articles/4591900-o-que-sao-custos-de-servicos-extra-e-como-faco-para-publica-los',
};

export const FlexibleCancellationHelpUrls = {
  [Language.de]:
    'https://answers.housinganywhere.com/de/articles/4680352-wie-funktioniert-die-flexible-stornierungsrichtlinie-fur-inserenten',
  [Language.en]:
    'https://answers.housinganywhere.com/en/articles/4680352-how-does-the-flexible-cancellation-policy-work-for-advertisers',
  [Language.es]:
    'https://answers.housinganywhere.com/es/articles/4680352-como-funciona-la-politica-de-cancelacion-flexible-para-los-anunciantes',
  [Language.fr]:
    'https://answers.housinganywhere.com/fr/articles/4680352-qu-est-ce-que-l-option-d-annulation-flexible-pour-les-annonceurs',
  [Language.it]:
    'https://answers.housinganywhere.com/it/articles/4680352-cosa-comporta-la-cancellazione-flessibile',
  [Language.nl]:
    'https://answers.housinganywhere.com/nl/articles/4680352-hoe-werken-de-flexibele-annuleringsvoorwaarden-voor-verhuurders',
  [Language.pt]:
    'https://answers.housinganywhere.com/pt/articles/4680352-como-funciona-a-politica-de-cancelamento-flexivel-para-os-anunciantes',
};

export const PaymentCalculationArticleUrls = {
  [Language.de]: 'https://answers.housinganywhere.com/de/articles/4967879',
  [Language.en]: 'https://answers.housinganywhere.com/en/articles/4967879',
  [Language.es]: 'https://answers.housinganywhere.com/es/articles/4967879',
  [Language.fr]: 'https://answers.housinganywhere.com/fr/articles/4967879',
  [Language.it]: 'https://answers.housinganywhere.com/it/articles/4967879',
  [Language.nl]: 'https://answers.housinganywhere.com/nl/articles/4967879',
};

const capitalizeFirst = (text: string): string =>
  text.substr(0, 1).toUpperCase() + text.substr(1);

interface Config {
  baseDomain: string;
  lang: Language;
}

export default class UrlResolver {
  private baseDomain: string;

  private lang: Language;

  static create = (config: Config): UrlResolver => new UrlResolver(config);

  constructor({ baseDomain, lang }: Config) {
    this.baseDomain = baseDomain;
    this.lang = lang;
  }

  applyFilterToSearchUrl = (pathname: string, slug?: string): string =>
    `${pathname}${slug ? `/${slug}` : ''}`;

  getBaseUrl() {
    return this.baseDomain;
  }

  absolutizePath(
    path: string,
    {
      skipAddLanguage = false,
      lang,
    }: { skipAddLanguage?: boolean; lang?: Language } = {},
  ) {
    if (isAbsoluteUrl(path)) {
      return path;
    }

    return skipAddLanguage
      ? `${this.getBaseUrl()}${path}`
      : `${this.getBaseUrl()}${this.formatHrefURL(path, lang)}`;
  }

  formatHrefURL(path: string, lang?: Language) {
    return formatHrefURL(path, lang || this.lang);
  }

  getAnswersUrl = (): string => answersLangToPath[this.lang];

  getBlogUrl = (): string => 'http://blog.housinganywhere.com';

  getBrowserNotSupportedUrl = (): string =>
    this.formatHrefURL('/browser-not-supported');

  getCareersUrl = (): string => 'https://careers.housinganywhere.com/';

  getDevelopersUrl = (): string => 'https://developers.housinganywhere.com/';

  getCommunityUrl = (lang: Language, category?: string): string =>
    this.formatHrefURL(
      `/${communityLangToPath[lang]}${category ? `?category=${category}` : ''}`,
      lang,
    );

  hasCommunityUrl = (lang: Language): boolean =>
    Boolean(communityLangToPath[lang]);

  getLandlordsCommunityUrl = (lang: Language): string =>
    this.formatHrefURL(`/${landlordsLangToPath[lang]}`, lang);

  hasLandlordsCommunityUrl = (lang: Language): boolean =>
    Boolean(landlordsLangToPath[lang]);

  getHelpCenterUrl = (lang: Language): string =>
    this.formatHrefURL(`/${helpCenterLangToPath[lang]}`, lang);

  hasHelpCenterUrl = (lang: Language): boolean =>
    Boolean(helpCenterLangToPath[lang]);

  getDashboardUrl() {
    return this.formatHrefURL('/my');
  }

  getShareRoomUrl = (
    roomId: number,
    { verified }: { verified?: boolean } = {},
  ): string =>
    this.formatHrefURL(
      `/my/share-room/${roomId}${buildParams({
        verified: verified ? 1 : null,
      })}`,
    );

  getListingSuccessUrl = (roomId: number | string): string =>
    this.formatHrefURL(`/my/listings/${roomId}/success`);

  getCalendarSyncGuideUrl = (): string =>
    calendarSyncGuidePathByLang[this.lang]
      ? this.absolutizePath(calendarSyncGuidePathByLang[this.lang])
      : this.absolutizePath(calendarSyncGuidePathByLang.en, {
          skipAddLanguage: true,
        });

  getFbAuthUrl = (searchParams: Record<string, unknown>): string =>
    `https://www.facebook.com/dialog/oauth${buildParams(searchParams)}`;

  getGoogleAuthUrl = (searchParams: Record<string, unknown>): string =>
    `https://accounts.google.com/o/oauth2/auth${buildParams(searchParams)}`;

  getFacebookShareUrl = (
    url: string,
    params: Record<string, unknown>,
  ): string =>
    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      url,
    )}${buildParams(params)}`;

  getBecomeAnAdvertiserUrl() {
    return this.formatHrefURL(
      becomeAnAdvertiserLangToPath[this.lang] ||
        becomeAnAdvertiserLangToPath.en,
    );
  }

  getLoginUrl(redirectAfterUrl?: string) {
    if (!redirectAfterUrl) {
      return this.formatHrefURL('/login');
    }

    return this.formatHrefURL(
      `/login${buildParams({ return_url: redirectAfterUrl })}`,
    );
  }

  getRecoverAccountUrl() {
    return this.formatHrefURL('/recover-account');
  }

  getRegisterUrl(redirectAfterUrl?: string) {
    if (!redirectAfterUrl) {
      return this.formatHrefURL('/register');
    }

    return this.formatHrefURL(
      `/register${buildParams({ return_url: redirectAfterUrl })}`,
    );
  }

  getRedirectUrl = (url: string): string => {
    const dashboardUrl = `${this.getBaseUrl()}${this.getDashboardUrl()}`;

    return !url || isAbsoluteUrl(url) || url.startsWith('//')
      ? dashboardUrl
      : url;
  };

  getCreateSearchAlertUrl = (alert: string, returnUrl: string): string =>
    this.formatHrefURL(
      `/my/callback/create-search-alert${buildParams({ alert, returnUrl })}`,
    );

  getSearchUrl = (
    city: string,
    country: string,
    filters?: Record<string, unknown>,
    category?: string,
  ): string => {
    let path = `/s/${encodeSearchQuery([city, country].join(', '))}`;

    if (category) {
      path = `${path}/${category}`;
    } else {
      path = `${path}${buildSearchQuery(filters)}`;
    }

    return this.formatHrefURL(path);
  };

  getCityUrl = (countrySlug: string, citySlug: string): string =>
    this.formatHrefURL(
      `/${capitalizeFirst(citySlug)}--${capitalizeFirst(countrySlug)}`,
    );

  getGuideUrl = (
    slug: string,
    countrySlug: string,
    citySlug?: string,
  ): string => {
    const url = citySlug
      ? `/${capitalizeFirst(citySlug)}--${capitalizeFirst(countrySlug)}/${slug}`
      : `/${capitalizeFirst(countrySlug)}/${slug}`;

    return this.formatHrefURL(url);
  };

  getCountryUrl = (countrySlug: string, category?: string): string => {
    const url = category
      ? `/${capitalizeFirst(countrySlug)}/${category}`
      : `/${capitalizeFirst(countrySlug)}`;

    return this.formatHrefURL(url);
  };

  getCountryGuideUrl = (countrySlug: string): string =>
    this.formatHrefURL(`/${capitalizeFirst(countrySlug)}`);

  getContactUrl = (lang?: Language): string =>
    this.formatHrefURL('/contact', lang);

  getUniversityUrl = (
    countrySlug: string,
    citySlug: string,
    universitySlug: string,
  ): string =>
    this.formatHrefURL(
      `/${capitalizeFirst(citySlug)}--${capitalizeFirst(
        countrySlug,
      )}/${universitySlug.toLowerCase()}`,
    );

  getPartnershipsUrl = (): string =>
    'https://info.housinganywhere.com/partnerships';

  getMyListingsUrl = (): string => this.formatHrefURL('/my/listings');

  getMyInboxUrl = ({
    state,
    listingId,
    text,
    page,
  }: {
    state?: keyof typeof ConversationsFilters;
    listingId?: string | number;
    text?: string;
    page?: number;
  } = {}): string =>
    this.formatHrefURL(
      `/my/inbox${buildParams({
        state: (state && ConversationsFilters[state]) || undefined,
        listingId,
        text,
        page,
      })}`,
    );

  getEditListingUrl = (
    listingId: number | string,
    isDuplicate?: boolean,
  ): string =>
    this.formatHrefURL(
      `/my/listings/${listingId}/edit${buildParams({ isDuplicate })}`,
    );

  getEditDraftListingUrl = (listingId: number | string): string =>
    this.formatHrefURL(`/my/listings/${listingId}/edit-draft`);

  getGetVerifiedUrl = (listingId: number | string): string =>
    this.formatHrefURL(`/my/get-verified${buildParams({ roomId: listingId })}`);

  getVerifyPhoneNumberUrl = (listingId: number | string): string =>
    this.formatHrefURL(`/my/verify-phone-number${buildParams({ listingId })}`);

  getTermsUrl = (): string => this.formatHrefURL('/terms');

  getLinkToUserPage = (userId: number | string): string =>
    this.formatHrefURL(`/users/show/${userId}`);

  getLinkToEditUserPage = (): string => this.formatHrefURL(`/my/profile/edit`);

  getPressUrl = (): string => this.formatHrefURL('/press-releases');

  getLegalUrl = (lang?: Language): string => this.formatHrefURL('/legal', lang);

  getCookiePolicyUrl = (): string => this.formatHrefURL('/cookie-policy');

  getDataSecurityUrl = (): string => this.formatHrefURL('/data-security');

  getSitemapUrl = (lang?: Language): string =>
    this.formatHrefURL('/sitemap', lang);

  getListingCalendarUrl = (id: number): string =>
    this.formatHrefURL(`/my/listings/${id}/calendar`);

  getListYourPlaceUrl = (): string => this.formatHrefURL('/list-your-place');

  getPaymentRequestHandleUrl = (handle: string): string =>
    this.absolutizePath(`/r/pay/${handle}`);

  getPaymentRequestHandleRelativeUrl = (handle: string): string =>
    this.formatHrefURL(`/r/pay/${handle}`);

  getConversationUrl = (
    conversationId: number | string,
    from?: string,
  ): string =>
    this.formatHrefURL(`/my/talk/${conversationId}${buildParams({ from })}`);

  getConversationBookingUrl = (conversationId: number | string): string =>
    this.formatHrefURL(`/my/talk/${conversationId}/book-request`);

  getPaymentUrl = (conversationId: number | string): string =>
    this.formatHrefURL(`/my/talk/${conversationId}/book`);

  getBillingUrl = (): string => this.formatHrefURL('/my/payouts/billing');

  getPaymentMethodsUrl = (): string =>
    this.formatHrefURL('/my/payments/methods');

  getPaymentsPaymentRequestsUrl = (): string =>
    this.formatHrefURL('/my/payments/requests');

  getPayoutMethodsUrl = (): string =>
    this.formatHrefURL('/my/payouts/preferences');

  getPayoutRoutingUrl = (payentMethodId: string): string =>
    this.formatHrefURL(`/my/payouts/preferences/${payentMethodId}/routing`);

  getInvoicesUrl = (): string => this.formatHrefURL('/my/payouts/invoices');

  getTransactionHistoryUrl = (): string =>
    this.formatHrefURL('/my/payouts/history');

  getPayoutsPaymentRequests = (tenancyUUID?: string): string =>
    this.formatHrefURL(
      `/my/payouts/payment-requests${
        tenancyUUID ? `?tenancy=${tenancyUUID}` : ''
      }`,
    );

  getPaymentPlan = (conversationId?: number | string): string =>
    this.formatHrefURL(
      `/my/payment-plan${conversationId ? `/${conversationId}` : ''}`,
    );

  getAcceptPaymentPlan = (
    inviteToken: string,
    tenantPreferredLanguage?: string,
  ): string =>
    this.formatHrefURL(
      `${
        tenantPreferredLanguage && tenantPreferredLanguage !== 'en'
          ? `/${tenantPreferredLanguage}`
          : ''
      }/payment-plan/${inviteToken}/accept`,
    );

  getPaymentPlanLanding = (
    inviteToken: string,
    tenantPreferredLanguage?: string,
  ): string =>
    this.formatHrefURL(
      `${
        tenantPreferredLanguage && tenantPreferredLanguage !== 'en'
          ? `/${tenantPreferredLanguage}`
          : ''
      }/payment-plan/${inviteToken}`,
    );

  getCommissionPlanUrl = (): string => this.formatHrefURL('/my/payouts/plan');

  getVerifyIdentityUrl = (): string =>
    this.formatHrefURL('/my/payouts/verify-identity');

  getPaymentResultUrl = (): string =>
    `${this.getBaseUrl()}${this.formatHrefURL('/adyenPaymentResult')}`;

  getPartnerRedirect = ({ roomid }: { roomid: number | string }): string =>
    `/partner-redirect${buildParams({
      roomid,
    })}`;

  getFacebookUrl = (): string => socialUrls.facebookUrl;

  getTwitterUrl = (): string => socialUrls.twitterUrl;

  getLinkedinUrl = (): string => socialUrls.linkedinUrl;

  getYoutubeUrl = (): string => socialUrls.youtubeUrl;

  getBookingsUrl = (): string => this.formatHrefURL('/my/bookings');

  getUserProfileUrl = (userId: number | string): string =>
    `/my/profile/${userId}`;

  getAdminPagesUrl = (): string => '/admin';

  getFavoritesUrl = (): string => this.formatHrefURL('/my/favorites');

  getSearchAlertsUrl = (): string => this.formatHrefURL('/my/searches');

  getHomeUrl = (): string => this.formatHrefURL('/');

  getRenting = (): string =>
    HowItWorksUrls.renting[this.lang] || HowItWorksUrls.renting.en;

  getRentingOut = (): string =>
    HowItWorksUrls.rentingOut[this.lang] || HowItWorksUrls.rentingOut.en;

  getPaymentsTenant = (): string =>
    PaymentsUrls.tenant[this.lang] || PaymentsUrls.tenant.en;

  getPaymentsLandlord = (): string =>
    PaymentsUrls.landlord[this.lang] || PaymentsUrls.landlord.en;

  getPricingTenantsUrl = (lang?: Language): string =>
    this.formatHrefURL(PricingUrls.tenant[lang || this.lang], lang);

  getPricingLandlordsUrl = (lang?: Language): string =>
    this.formatHrefURL(PricingUrls.landlord[lang || this.lang], lang);

  getRentRadarUrl = (): string =>
    `https://rentradar.housinganywhere.com/${
      rentRadarLanguangesWithLeadFormSupported.includes(this.lang)
        ? 'lead/'
        : ''
    }${rentRadarLanguanges.includes(this.lang) ? this.lang : ''}`;

  getKrugerPRUrl = (): string =>
    krugerLangToPath[this.lang] || krugerLangToPath.en;

  getRenmateFAQUrl = (): string =>
    'https://answers.housinganywhere.com/en/articles/3732114-how-will-the-rentmate-acquisition-affect-me';

  getStanzaZooArticleUrl = (): string =>
    this.formatHrefURL(
      '/housinganywhere_lannuncia_acquisizione_di_stanzazoo',
      Language.it,
    );

  getCoronaFAQUrl = (): string =>
    'https://answers.housinganywhere.com/en/articles/3746319-coronavirus-cancellation-policy';

  getCancellationPolicyHelpUrl = (): string =>
    CancellationPolicyHelpUrls[this.lang] || CancellationPolicyHelpUrls.en;

  getStructuredCostsHelpUrl = (): string =>
    StructuredCostsHelpUrls[this.lang] || StructuredCostsHelpUrls.en;

  getFlexibleCancellationHelpUrl = (): string =>
    FlexibleCancellationHelpUrls[this.lang] || FlexibleCancellationHelpUrls.en;

  getPaymentCalculationArticleUrl = (): string =>
    PaymentCalculationArticleUrls[this.lang] ||
    PaymentCalculationArticleUrls.en;

  getAdvancedPricingSpecialOfferArticle = (): string =>
    AdvancedPricingSpecialOfferArticle[this.lang] ||
    AdvancedPricingSpecialOfferArticle.en;

  getAdvancedPricingBannerLink = (): string =>
    AdvancedPricingBannerLink[this.lang] || AdvancedPricingBannerLink.en;

  getAdvancedPricingDescriptionLink = (): string =>
    AdvancedPricingDescriptionLink[this.lang] ||
    AdvancedPricingDescriptionLink.en;

  getContactAdvertiserUrl = (
    listingId: number,
    params: { startDate?: string | null; endDate?: string | null },
    currentStep?: BookingFlowSteps,
  ): string =>
    this.formatHrefURL(
      `/my/book/contact/${listingId}${
        currentStep ? `/${currentStep}` : ''
      }${buildParams(params)}`,
    );

  getSecurePaymentUrl = (): string => {
    switch (this.lang) {
      case Language.fr:
        return this.formatHrefURL('/paiements-securise', this.lang);
      case Language.de:
        return this.formatHrefURL('/sichere-bezahlung', this.lang);
      case Language.es:
        return this.formatHrefURL('/pagos-seguros', this.lang);
      case Language.it:
        return this.formatHrefURL('/pagamenti-sicuri', this.lang);
      case Language.nl:
        return this.formatHrefURL('/veilige-huurbetalingen', this.lang);
      default:
        return this.formatHrefURL('/secure-payments', Language.en);
    }
  };

  getOnlineRentPayment = (): string => {
    switch (this.lang) {
      case Language.fr:
        return this.formatHrefURL('/paiement-locatif-en-ligne', this.lang);
      case Language.de:
        return this.formatHrefURL('/online-mietzahlung', this.lang);
      case Language.es:
        return this.formatHrefURL('/pago-alquiler-online', this.lang);
      case Language.it:
        return this.formatHrefURL('/pagamento-affitto-online', this.lang);
      case Language.nl:
        return this.formatHrefURL('/online-huur-betalen', this.lang);
      default:
        return this.formatHrefURL('/online-rent-payment', Language.en);
    }
  };

  /**
   * Checks if current url is one of "/my" pages, excluding booking flow
   */
  isOnAccountPages = (pathname: string): boolean =>
    !pathname.includes('my/book/contact') &&
    !pathname.match(/my\/talk\/\d+\/book-request/) &&
    (Boolean(matchPattern('/my', pathname)) ||
      Boolean(matchPattern('/:lang/my', pathname)));

  getHomePageDataUrl = (lang: Language): string =>
    `/${lang === Language.en ? '' : `${lang}/`}home`;

  getAbsBookingLinkUrl = (linkId: string): string =>
    this.absolutizePath(`/link/${linkId}`);
}
